<template>
  <div class="p-3">
    <div class="d-flex flex-column gap-3">
      <!-- Help text -->
      <div class="help-text small text-muted">
        Deferring a {{ noun }} (previously called "whitelisting") removes it from view, either temporarily or
        indefinitely. This is commonly used to handle {{ noun }}s that are under review or fall outside the scope of
        local STR regulations.
      </div>

      <div class="rounded bg-light rounded p-3 d-flex flex-column gap-2">
        <div class="d-flex flex-row align-items-center gap-2">
          <div class="fw-medium me-auto">
            <template v-if="model.isWhitelisted">
              Deferred {{ model.deferralEndDate ? `until ${model.deferralEndDate.format('MMM DD, YYYY')}` : 'indefinitely' }}
            </template>
            <template v-else-if="model.hasEndedDeferral">
              Deferral ended {{ model.deferralEndDate ? model.deferralEndDate.format('MMM DD, YYYY') : '' }}
            </template>
            <template v-else>
              Not deferred
            </template>
          </div>
          <button v-if="!model.isWhitelisted" type="button" class="btn btn-sm btn-warning" @click="openModal('defer')">
            Defer...
          </button>
          <template v-else>
            <button type="button" class="btn btn-sm btn-outline-dark" @click="openModal('defer')">
              Edit...
            </button>
            <button type="button" class="btn btn-sm btn-outline-dark" @click="openModal('resume')">
              Resume
            </button>
          </template>
        </div>
        <div v-if="model.hasDeferral && model.whitelistReason" class="ps-2 border-start border-2">
          {{ model.whitelistReason }}
        </div>
      </div>
      <div v-if="model.whitelistUpdatedBy" class="small text-muted">
        Updated by {{ model.whitelistUpdatedBy.displayName }}
        <template v-if="model.whitelistUpdatedAt">
          on {{ model.whitelistUpdatedAt.format('M/D/YY h:mm a') }}
        </template>
      </div>
    </div>

    <ModalView
      v-model="editing"
      :title="`${isResumeMode ? 'Resume' : 'Defer'} this ${noun}`"
      :ok-title="isResumeMode ? 'Resume' : 'Defer'"
      :ok-variant="isResumeMode ? undefined : 'warning'"
      @ok="commit"
      @cancel="cancel">
      <p v-if="isResumeMode">
        This will remove the deferral and the {{ noun }} will appear in reports again.
      </p>
      <div class="d-flex flex-column gap-3">
        <div>
          <label class="form-label">Note</label>
          <BTextarea v-model="note" :disabled="saving"/>
        </div>
        <template v-if="!isResumeMode">
          <div>
            <BCheckbox v-model="indefinite" label="Defer indefinitely" flush />
          </div>
          <div v-if="!indefinite">
            <label class="form-label">End date</label>
            <input type="date" class="form-control" v-model="expiration" />
          </div>
        </template>
      </div>
    </ModalView>
  </div>
</template>

<script>
import BTextarea from "@/components/common/BTextarea.vue";
import ModalView from "@/components/common/ModalView.vue";
import BCheckbox from "@/components/common/BCheckbox.vue";
import {PersistentModel} from "@/models/PersistentModel";

export default {
  components: {BCheckbox, ModalView, BTextarea},
  props: {
    model: PersistentModel,
    noun: String
  },
  data() {
    return {
      editMode: null,
      note: null,
      indefinite: false,
      expiration: null,
      saving: false
    };
  },
  watch: {
    model: {
      immediate: true,
      handler() {
        this.reset();
      }
    }
  },
  computed: {
    canUpdate() {
      return !this.saving;
    },
    changed() {
      const expiration = this.model.whitelistExpiration;
      return this.note !== this.model.whitelistReason
        || this.expiration !== (expiration?.format('YYYY-MM-DD') || null);
    },
    editing: {
      get() {
        return Boolean(this.editMode);
      },
      set(editing) {
        if (!editing) {
          this.editMode = null;
        }
      }
    },
    isResumeMode() {
      return this.editMode === 'resume';
    }
  },
  methods: {
    openModal(mode) {
      this.note = '';
      this.editMode = mode;
    },
    closeModal() {
      this.editMode = null;
    },
    async commit() {
      const whitelisted = !this.isResumeMode;
      const reason = this.note;
      const expiration = whitelisted && !this.indefinite ? this.expiration : null;

      try {
        this.saving = true;

        // Update the model
        await this.model.updateWhitelisted(whitelisted, reason, expiration);
      } finally {
        this.saving = false;
        this.closeModal();
      }
    },
    reset() {
      this.note = this.model.whitelistReason;
      const expiration = this.model.whitelistExpiration;
      this.indefinite = !expiration;
      this.expiration = expiration ? expiration.format('YYYY-MM-DD') : null;
    },
    cancel() {
      this.reset();
      this.closeModal();
    }
  }
}
</script>

<style>
.help-text {
  font-size: 0.8em;
  color: var(--bs-gray);
}
</style>
